import { GoogleGenAI } from "@google/genai";
import { Booth, SocialPost, SocialTrend, ContentType, Language, Tone } from '../types';

const modelName = 'gemini-2.5-flash';

// Event constant for the UI to listen to
export const AUTH_ERROR_EVENT = 'gemini_auth_error';

const triggerAuthError = () => {
  if (typeof window !== 'undefined') {
    console.warn("[GeminiService] Triggering Auth Error Event");
    window.dispatchEvent(new Event(AUTH_ERROR_EVENT));
  }
};

// Helper to safely retrieve API Key from various environment configurations (Vite, Webpack, Node)
const getApiKey = (): string | undefined => {
  let key: string | undefined;

  // 1. Try Vite (import.meta.env) - Primary for Cloudflare Pages
  try {
    // Vite replaces import.meta.env variables at build time
    // Cast to any to avoid TS errors regarding env property on ImportMeta
    const meta = import.meta as any;
    if (meta.env && meta.env.VITE_API_KEY) {
      key = meta.env.VITE_API_KEY;
      console.log("[GeminiService] API Key detected in VITE_API_KEY");
    }
  } catch (e) {
    // Ignore if import.meta is not available
  }

  // 2. Fallback for other environments (avoiding direct 'process' reference to fix TS2580)
  if (!key) {
    try {
      // Use type casting to access global process safely without requiring @types/node
      const globalProcess = (typeof globalThis !== 'undefined' ? globalThis : window) as any;
      if (globalProcess?.process?.env?.API_KEY) {
        key = globalProcess.process.env.API_KEY;
        console.log("[GeminiService] API Key detected in process.env.API_KEY");
      }
    } catch (e) {
      // Ignore errors accessing process
    }
  }

  if (!key) {
    console.error("[GeminiService] CRITICAL: No API Key found. Please set VITE_API_KEY in Cloudflare Pages settings.");
    triggerAuthError();
  }

  return key;
};

const handleApiError = (error: any): string => {
  console.error("[GeminiService] API Error:", error);
  const errorMessage = error.message || error.toString();
  
  if (errorMessage.includes('API key') || errorMessage.includes('403') || errorMessage.includes('400') || errorMessage.includes('key not found')) {
    triggerAuthError();
    return "AUTHENTICATION FAILED: The War Room AI cannot access the Gemini API. Please check your API Key configuration.";
  }
  
  return `AI Service Error: ${errorMessage}`;
};

export const getBoothStrategy = async (booth: Booth): Promise<string> => {
  const apiKey = getApiKey();
  
  if (!apiKey) {
    return "CONFIGURATION REQUIRED: API Key is missing. Check the pop-up for instructions.";
  }

  const ai = new GoogleGenAI({ apiKey });
  
  const prompt = `
    Role: You are a Senior Political Strategist for the BJP in Assam, advising candidate Shashibhusan Deka for the Borkhetri constituency.
    Context: Analyze the following polling booth data. 
    Booth: ${booth.name} (Booth No: ${booth.number}).
    Demographics: ${booth.demographics.map(d => `${d.community} (${d.percentage}%)`).join(', ')}.
    Key Issues: ${booth.keyIssues.join(', ')}.
    Past Trend: ${booth.lastElectionTrend}.
    Organizational Strength: ${booth.verifiedPannaPramukhs} verified Panna Pramukhs out of target ${booth.targetPannaPramukhs}.

    Task: Provide a 3-point micro-strategy to maximize votes in this booth.
    1. Specifically mention how to leverage the 'Orunodoi' or 'PMAY' schemes if relevant.
    2. If the area is Indigenous Assamese, suggest cultural connect (Namghar/Bihu).
    3. If there is a Panna Pramukh deficit, suggest RSS Gatnayak coordination.
    
    Keep it concise and actionable.
  `;

  try {
    const response = await ai.models.generateContent({
      model: modelName,
      contents: prompt,
    });
    return response.text || "Analysis unavailable.";
  } catch (error: any) {
    return handleApiError(error);
  }
};

export const getConstituencyOverview = async (booths: Booth[]): Promise<string> => {
  const apiKey = getApiKey();

  if (!apiKey) {
    return "CONFIGURATION REQUIRED: API Key is missing.";
  }

  const ai = new GoogleGenAI({ apiKey });
  const boothSummary = booths.map(b => `${b.name}: ${b.lastElectionTrend}`).join(', ');
  
  const prompt = `
    Role: Chief Strategist for BJP Assam.
    Context: Borkhetri Constituency 2026 Election.
    Data: We have data for ${booths.length} key booths. Trends: ${boothSummary}.
    
    Task: Write a short, motivating executive summary for the Candidate Shashibhusan Deka. 
    Highlight the balance between development politics (Labharthi) and identity (Satra/Culture). 
    Mention the threat of river erosion as a key talking point.
  `;

  try {
    const response = await ai.models.generateContent({
      model: modelName,
      contents: prompt,
    });
    return response.text || "Overview unavailable.";
  } catch (error: any) {
    return handleApiError(error);
  }
};

export const getSocialMediaAnalysis = async (posts: SocialPost[], trends: SocialTrend[]): Promise<string> => {
  const apiKey = getApiKey();

  if (!apiKey) {
    return "CONFIGURATION REQUIRED: API Key is missing.";
  }

  const ai = new GoogleGenAI({ apiKey });
  
  const postsText = posts.map(p => `[${p.platform}] ${p.sentiment}: ${p.content}`).join('\n');
  const trendsText = trends.map(t => `${t.hashtag} (${t.volume} mentions)`).join(', ');

  const prompt = `
    Role: Digital Media Manager for BJP Assam.
    Context: Social Media Feed for Borkhetri Constituency.
    Trending: ${trendsText}
    Recent Posts:
    ${postsText}

    Task: Provide a "Daily Digital Strategy" (Bullet points).
    1. What is the sentiment on the ground?
    2. Which hashtag should our IT Cell push today?
    3. Suggest one viral content idea based on the positive posts.
    4. Flag any negative risks.
  `;

  try {
    const response = await ai.models.generateContent({
      model: modelName,
      contents: prompt,
    });
    return response.text || "Social analysis unavailable.";
  } catch (error: any) {
    return handleApiError(error);
  }
};

export const generateCounterNarrative = async (negativeTopic: string): Promise<string> => {
  const apiKey = getApiKey();

  if (!apiKey) {
    return "CONFIGURATION REQUIRED: API Key is missing.";
  }

  const ai = new GoogleGenAI({ apiKey });
  
  const prompt = `
    Role: Crisis Manager for BJP Candidate Shashibhusan Deka.
    Context: A negative rumor or issue is spreading: "${negativeTopic}".
    
    Task: Draft a short, powerful counter-narrative response for WhatsApp and Twitter.
    Tone: Professional, factual, firm, but polite.
    Key Elements to include:
    - Mention "Double Engine Sarkar" development.
    - Ask voters to trust official sources (Shakti Kendra Probharis).
    - Use relevant hashtags like #BorkhetriPragati.
    
    Output Format:
    **WhatsApp Message:** [Draft]
    **Twitter Reply:** [Draft]
  `;

  try {
    const response = await ai.models.generateContent({
      model: modelName,
      contents: prompt,
    });
    return response.text || "Could not generate counter-narrative.";
  } catch (error: any) {
    return handleApiError(error);
  }
};

export const generateCampaignContent = async (
  type: ContentType,
  topic: string,
  language: Language,
  tone: Tone
): Promise<string> => {
  const apiKey = getApiKey();

  if (!apiKey) {
    return "CONFIGURATION REQUIRED: API Key is missing.";
  }

  const ai = new GoogleGenAI({ apiKey });

  const prompt = `
    Role: Campaign Content Writer for Shashibhusan Deka (BJP, Borkhetri Constituency, Assam).
    Task: Write a ${type} about "${topic}".
    Language: ${language} ${language === 'Assamese' ? '(Use Assamese script)' : ''}.
    Tone: ${tone}.
    
    Context:
    - Focus on development (Vikas), cultural heritage (Satra/Namghar), and stability.
    - Use local context relevant to Assam politics (Orunodoi, Floods, Identity).
    - If it's a speech, keep it engaging for a rally.
    - If it's a WhatsApp message, include emojis and hashtags.
  `;

  try {
    const response = await ai.models.generateContent({
      model: modelName,
      contents: prompt,
    });
    return response.text || "Content generation failed.";
  } catch (error: any) {
    return handleApiError(error);
  }
};